// LEGACY CV export flow (SP-12046): do not modify, do not import outside index.ts.
// Remove with the cv_export_legacy_flow feature flag.
import { Button, ButtonKind } from "PFComponents/button";
import { LoadingDots } from "PFComponents/loading_dots";
import { Tooltip } from "PFComponents/tooltip";
import useIsFeatureEnabled from "PFCore/helpers/use_is_feature_enabled";
import { useIsCurrentUserPermittedTo } from "PFCore/helpers/use_is_permitted_to";
import { IconName } from "PFTheme/graphics/icons";
import { Collection, FeatureFlag, PermissionRule, Profile } from "PFTypes";
import React, { lazy, Suspense, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import css from "./lazy_local_export_trigger.module.scss";
import { ExportProfileCollection, GetProfilesCollection } from "./local_export_trigger.types";

const LocalExportTrigger = lazy(() =>
  import("./local_export_trigger").then(({ LocalExportTrigger }) => ({ default: LocalExportTrigger }))
);

type LazyLocalExportTriggerProps = {
  quantity?: number;
  getProfilesCollection: GetProfilesCollection;
  getRawCollection?: () => Promise<Collection<Profile[]>>;
  isBlindModeOn?: boolean;
  buttonKind: ButtonKind;
  icon?: IconName;
  label?: string;
  tooltipContent?: string;
  qaId?: string;
  triggerButtonRef?: React.RefObject<HTMLButtonElement | null>;
};

export const LazyLocalExportTrigger = ({
  quantity,
  getProfilesCollection,
  getRawCollection,
  isBlindModeOn = false,
  buttonKind = "primary",
  icon,
  label,
  tooltipContent,
  qaId,
  triggerButtonRef
}: LazyLocalExportTriggerProps) => {
  const { t } = useTranslation(["translation", "legacyCvExport"]);
  const [profilesCollection, setProfilesCollection] = useState<ExportProfileCollection | null>(null);
  const [rawCollection, setRawCollection] = useState<Collection<Profile[]> | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const isEnabled = useIsFeatureEnabled();
  const isProfileExportEnabled = isEnabled(FeatureFlag.ProfileExporter);

  const canExportDocs = useIsCurrentUserPermittedTo(PermissionRule.ExportDocuments);

  const Wrapper = useMemo(
    () =>
      tooltipContent
        ? ({ children }) => <Tooltip content={tooltipContent}>{children}</Tooltip>
        : React.Fragment,
    [tooltipContent]
  );

  if (!isProfileExportEnabled || !canExportDocs) {
    return null;
  }

  if (quantity === 0) {
    return null;
  }

  if (profilesCollection && (rawCollection || !getRawCollection)) {
    return (
      <Suspense /* no fallback to avoid page refresh on LocalExportTrigger loading */>
        <LocalExportTrigger
          handleModalClose={() => {
            setProfilesCollection(null);
            setIsLoading(false);
          }}
          profilesCollection={profilesCollection}
          rawDataCollection={rawCollection}
          getProfilesCollection={getProfilesCollection}
          isBlindModeOn={isBlindModeOn}
          icon={icon}
          label={label}
          buttonKind={buttonKind}
          qaId={qaId}
        />
      </Suspense>
    );
  }

  if (isLoading) {
    return <LoadingDots className={css.loadingDots} />;
  }

  return (
    // eslint-disable-next-line react-hooks/static-components
    <Wrapper>
      <Button
        ref={triggerButtonRef}
        icon={icon}
        text={label || (!icon ? t("legacyCvExport:exportFiles.export") : undefined)}
        kind={buttonKind}
        onClick={() => {
          setIsLoading(true);
          getRawCollection &&
            getRawCollection().then((resp) => {
              setRawCollection(resp);
            });
          getProfilesCollection().then((resp) => {
            setProfilesCollection(resp ? resp : null);
            setIsLoading(false);
          });
        }}
        qaId={qaId}
      />
    </Wrapper>
  );
};
